import React from 'react';

import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Image,
  Input,
  Label,
  LoadingMask,
  Paragraph,
  Text,
} from '@zillow/constellation-10';

import { formatDate } from '@common/paymentsUtils/dateUtils';
import Page from '@renter-hub/components/RenterPage';
import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import MoveInCostsTypes from '@common/constants/MoveInCostsConstants';
import PontoonConstants from '@common/constants/PontoonExceptionConstants';
import OnboardingStatus from '@renter-hub/constants/onboardingStatus';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import REQUEST_STATUS from '@common/constants/requestStatus';
import { LandlordBlockCard } from '@renter-hub/modules/LandlordBlockCard/LandlordBlockCard.component';
import type { MoveInCosts } from '@renter-hub/models/Types';
import HTTP_STATUS from '@common/constants/HttpConstants';
import StringConstants from '@common/constants/StringConstants';
import DTWrenchIcon from '@assets/dt-icon-wrench.svg'; // TODO: replace with constellation lib
import { detectCombinedNames } from '@common/utils/detectCombinedNames';
import errorMessages from '@common/constants/errorMessages';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface ConfirmRentDetailsPageProps {
  requestStatus: {
    rentDetails: string;
  };
  rentDetails: {
    name: string;
    firstName: string;
    email: string;
    address: {
      homeAddress: string;
      unit?: string;
      city: string;
      state: string;
      zipCode: string;
    };
    amount: string;
    firstDueDate: string;
    lastDueDate: string;
    monthToMonth: boolean;
    moveInCosts?: MoveInCosts;
    thumbnail?: any;
    renterFirstName: string;
    renterLastName: string;
  };
  onboardingStatus: string;
  paymentReportingOptedIn: boolean | null;
  featureFlagEnableRentReporting: boolean;
  requestRentDetails: () => any;
  registerRenter: (featureFlagEnableRentReporting: boolean, firstName: string | null, lastName: string | null) => any;
  submitCorrectionRequest: () => void;
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}

interface ConfirmRentDetailsPageState {
  renderNonMatchingInvite: boolean;
  correctEmail: string;
  invitationUrl: string;
  updatedRenterFirstName: {
    value: string;
    error: string;
  };
  updatedRenterLastName: {
    value: string;
    error: string;
  };
  isLoading: boolean;
}

class ConfirmRentDetailsPage extends React.Component<ConfirmRentDetailsPageProps, ConfirmRentDetailsPageState> {
  state = {
    renderNonMatchingInvite: false,
    correctEmail: '',
    invitationUrl: '',
    updatedRenterFirstName: {
      value: '',
      error: '',
    },
    updatedRenterLastName: {
      value: '',
      error: '',
    },
    isLoading: true,
  };

  componentDidMount() {
    Promise.resolve()
      .then(() => this.props.requestRentDetails())
      .then((response) => {
        if (response.data.httpStatus === HTTP_STATUS.SC_ACCEPTED) {
          this.setState({
            renderNonMatchingInvite: true,
            correctEmail: response.data.email,
            invitationUrl: response.data.invitationUrl,
          });
        }
        this.setState({
          updatedRenterFirstName: {
            value: response.data.renterFirstName,
            error: detectCombinedNames(response.data.renterFirstName)
              ? errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM
              : '',
          },
          updatedRenterLastName: {
            value: response.data.renterLastName,
            error: detectCombinedNames(response.data.renterLastName)
              ? errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM
              : '',
          },
        });
      })
      .finally(() => this.setState({ isLoading: false }));
  }

  onSubmit = () => {
    const updatedFirstName = this.state.updatedRenterFirstName.value;
    const updatedLastName = this.state.updatedRenterLastName.value;
    const existingFirstName = this.props.rentDetails.renterFirstName;
    const existingLastName = this.props.rentDetails.renterLastName;

    RachelCSEvents.emitClickConfirmDetailsEvent({
      user: this.props.user,
    });

    this.props.registerRenter(
      this.props.featureFlagEnableRentReporting,
      updatedFirstName != existingFirstName ? updatedFirstName : null,
      updatedLastName != existingLastName ? updatedLastName : null,
    );
  };

  onEditFirstName = (e) => {
    let error = '';
    if (detectCombinedNames(e.currentTarget.value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM;
    }
    this.setState({
      updatedRenterFirstName: {
        value: e.currentTarget.value,
        error,
      },
    });
  };

  onEditLastName = (e) => {
    let error = '';
    if (detectCombinedNames(e.currentTarget.value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT_CONFIRM;
    }
    this.setState({
      updatedRenterLastName: {
        value: e.currentTarget.value,
        error,
      },
    });
  };

  renderMoveInCosts(moveInCosts: MoveInCosts) {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'token(spacing.sm)',
        }}
      >
        <Box>
          <Text textStyle="body-bold">Move-in payment: </Text>
          <Text className="fs-exclude">
            Due on{' '}
            {formatDate(
              moveInCosts.dueDate,
              'MMMM d, yyyy',
              PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.MOVE_IN_COSTS.DUE_DATE,
            )}
          </Text>
        </Box>
        {moveInCosts.moveInCostsItems.map((moveInCostItem, i) => (
          <Box
            key={`moveInCosts${i}`}
            css={{
              display: 'grid',
              gridTemplateColumns: '9fr 3fr',
              marginBottom: 'token(spacing.xs) !important',
            }}
          >
            <Box>
              <Paragraph textStyle="body-sm">
                {moveInCostItem.subType || MoveInCostsTypes[moveInCostItem.type]}
              </Paragraph>
              <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                {moveInCostItem.notes}
              </Text>
            </Box>
            <Box css={{ textAlign: 'right' }}>
              <Text textStyle="body-sm">{centsToDollars(moveInCostItem.amount)}</Text>
            </Box>
          </Box>
        ))}
        <Divider />
        <Box css={{ display: 'grid', gridTemplateColumns: '9fr 3fr' }} className="fs-exclude">
          <Paragraph textStyle="body-bold">Total payment</Paragraph>
          <Box css={{ textAlign: 'right' }}>
            <Text textStyle="body-bold">{centsToDollars(moveInCosts.amount)}</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  renderNonMatchingInviteVariation = () => {
    const supportEmail = StringConstants.LINKS.SUPPORT_EMAIL.zillow;
    const { correctEmail, invitationUrl } = this.state;
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: { base: '1fr', md: '1fr 11fr' },
          gap: '16px',
          paddingTop: 'token(spacing.md) !important',
        }}
      >
        <Box
          css={{
            display: { base: 'none', md: 'block' },
            textAlign: 'center',
            height: '60px',
            padding: '16px',
          }}
        >
          <Image src={DTWrenchIcon as unknown as string} alt="Wrench Icon" />
        </Box>
        <Box>
          <Heading level={3} textStyle="heading-xs">
            This invitation is intended for&nbsp;
            <Anchor href={`mailto:${correctEmail}`}>{correctEmail}</Anchor>. Please create or login with this
            account.&nbsp;
          </Heading>
          <Text>
            Still have questions?&nbsp;
            <Anchor href={`mailto:${supportEmail}`}>Contact customer service</Anchor>.
          </Text>
          <Box css={{ marginTop: 'token(spacing.md) !important' }}>
            <Anchor href={invitationUrl}>
              <Button emphasis="filled" size="md">
                Create/Login
              </Button>
            </Anchor>
          </Box>
        </Box>
      </Box>
    );
  };

  renderBody() {
    const { rentDetails, onboardingStatus } = this.props;
    const { homeAddress, unit, city, state } = rentDetails.address;
    const formattedAddress = `${homeAddress} ${unit ? `${unit} ` : ''}${city}, ${state}`;
    const { renderNonMatchingInvite } = this.state;
    const requestStatus = this.props.requestStatus.rentDetails;
    const { moveInCosts } = rentDetails;
    if (onboardingStatus === OnboardingStatus.REJECTED) {
      return <LandlordBlockCard isDashboard={false} />;
    } else if (renderNonMatchingInvite) {
      return this.renderNonMatchingInviteVariation();
    } else if (requestStatus === REQUEST_STATUS.SUCCESS) {
      return (
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'token(spacing.sm)' }}>
          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'token(spacing.md)',
              gridColumnEnd: { base: 'span 12', md: 'span 8' },
            }}
          >
            <Box>
              <Heading level={1} textStyle="heading-xs" css={{ marginBottom: 'token(spacing.xs) !important' }}>
                Confirm payment details
              </Heading>
              <Paragraph css={{ marginBottom: 'token(spacing.md) !important' }}>
                These are the details that {rentDetails.name} set up. If anything&apos;s incorrect, contact them to
                request an update.
              </Paragraph>
              <Box css={{ display: 'grid', flexDirection: 'column', gap: 'token(spacing.md)' }}>
                <Card css={{ padding: 'token(spacing.md) !important' }} elevated={false}>
                  <Box css={{ marginBottom: 'token(spacing.md) !important' }}>
                    <Text textStyle="body-bold">Your rental: </Text>
                    <Text>{formattedAddress}</Text>
                  </Box>
                  <Box
                    css={{
                      display: 'grid',
                      gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
                      gap: 'token(spacing.md)',
                    }}
                  >
                    <Box>
                      <Paragraph>Monthly rent</Paragraph>
                      <Paragraph textStyle="body-bold">{centsToDollars(parseInt(rentDetails.amount, 10))}</Paragraph>
                    </Box>
                    <Box>
                      <Paragraph>Due date</Paragraph>
                      <Paragraph textStyle="body-bold">
                        {formatDate(
                          rentDetails.firstDueDate,
                          'do',
                          PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.RENT_PAYMENT.FIRST_DUE_DATE,
                        )}{' '}
                        of the month
                      </Paragraph>
                    </Box>
                    <Box>
                      <Paragraph>Pay to</Paragraph>
                      <Paragraph textStyle="body-bold">{rentDetails.name}</Paragraph>
                    </Box>
                    <Box>
                      <Paragraph>Payment period</Paragraph>
                      <Paragraph textStyle="body-bold">
                        {formatDate(
                          rentDetails.firstDueDate,
                          'MMMM d, yyyy',
                          PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.RENT_PAYMENT.FIRST_DUE_DATE,
                        )}
                        &nbsp;
                        {rentDetails.monthToMonth
                          ? '- No end date/Month-to-month'
                          : `to ${formatDate(
                              rentDetails.lastDueDate,
                              'MMMM d, yyyy',
                              PontoonConstants.RENTER_HUB.CONFIRM_RENT_DETAILS.RENT_PAYMENT.LAST_DUE_DATE,
                            )}`}
                      </Paragraph>
                    </Box>
                  </Box>
                </Card>

                {moveInCosts && moveInCosts.moveInCostsItems && moveInCosts.moveInCostsItems.length > 0 && (
                  <Card css={{ padding: 'token(spacing.md) !important' }} elevated={false}>
                    {this.renderMoveInCosts(moveInCosts)}
                  </Card>
                )}

                <Card css={{ padding: 'token(spacing.md) !important' }} elevated={false}>
                  <Paragraph textStyle="body-bold" css={{ marginBottom: 'token(spacing.sm) !important' }}>
                    Verify your name and correct any errors
                  </Paragraph>
                  <Box
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 'token(spacing.sm) !important',
                      justifyContent: 'space-between',
                      lg: {
                        flexDirection: 'row',
                      },
                    }}
                  >
                    <FormField
                      css={{ flexBasis: '50%', width: '100%' }}
                      control={
                        <Input
                          css={{ padding: '10px 16px 10px 16px !important' }}
                          onChange={this.onEditFirstName}
                          value={this.state.updatedRenterFirstName.value}
                        />
                      }
                      error={!!this.state.updatedRenterFirstName.error}
                      label={<Label>First name</Label>}
                      description={
                        this.state.updatedRenterFirstName.error ? (
                          <FormHelp css={{ marginTop: '4px !important' }} error>
                            {this.state.updatedRenterFirstName.error}
                          </FormHelp>
                        ) : null
                      }
                    />
                    <FormField
                      css={{ flexBasis: '50%', width: '100%' }}
                      control={
                        <Input
                          css={{ padding: '10px 16px 10px 16px !important' }}
                          onChange={this.onEditLastName}
                          value={this.state.updatedRenterLastName.value}
                        />
                      }
                      error={!!this.state.updatedRenterLastName.error}
                      label={<Label>Last name</Label>}
                      description={
                        this.state.updatedRenterLastName.error ? (
                          <FormHelp css={{ marginTop: '4px !important' }} error>
                            {this.state.updatedRenterLastName.error}
                          </FormHelp>
                        ) : null
                      }
                    />
                  </Box>
                </Card>
                <Box>
                  <ButtonGroup
                    aria-label="confirm actions"
                    role="group"
                    css={{ marginBottom: 'token(spacing.xs) !important' }}
                  >
                    <Button emphasis="filled" onClick={this.onSubmit}>
                      Confirm details
                    </Button>
                    <Button asChild>
                      <a href={`mailto:${rentDetails.email}`}>Contact {rentDetails.firstName || ''}</a>
                    </Button>
                  </ButtonGroup>
                  <Text css={{ color: 'text.subtle' }} textStyle="fineprint">
                    By clicking <Text textStyle="fineprint-bold">Confirm details</Text>, you agree to comply with
                    the&nbsp;
                    <Anchor target="_blank" href="https://www.zillow.com/corp/Terms.htm">
                      Terms of Use
                    </Anchor>
                    ,&nbsp;
                    <Anchor target="_blank" href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms">
                      Rental User Terms
                    </Anchor>
                    , and our third-party payment processor&apos;s&nbsp;
                    <Anchor target="_blank" href="https://stripe.com/us/privacy">
                      Payment Terms&nbsp;
                    </Anchor>
                    and&nbsp;
                    <Anchor target="_blank" href="https://plaid.com/legal">
                      Bank Authorization Terms.
                    </Anchor>
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    } else if (requestStatus === REQUEST_STATUS.FAIL) {
      return (
        <div className="pal">
          <Text>
            Could not load page.&nbsp;
            <Anchor type="primary" href="/renter-hub">
              Return to renter hub
            </Anchor>
          </Text>
        </div>
      );
    }
  }

  render() {
    return (
      <Page.MainSection>
        <HelmetContainer {...SeoConstants.confirmRentDetails} />
        <LoadingMask loading={this.state.isLoading} loadingVoiceOver="Loading" role="status">
          {this.renderBody()}
        </LoadingMask>
      </Page.MainSection>
    );
  }
}

export default ConfirmRentDetailsPage;
